import Fuse from 'fuse.js';
import { IndustryOption } from './types';
import { INDUSTRY_OPTIONS } from './industryOptions';

const MAX_RESULTS = 8;

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

// True if the query matches the start of any whitespace-separated word in the text, e.g.
// "estate" matching the second word of "Real Estate".
function matchesWordBoundary(text: string, normalizedQuery: string): boolean {
  return text
    .split(/\s+/)
    .some((word) => word.toLowerCase().startsWith(normalizedQuery));
}

type MatchTier = 'exact' | 'prefix' | 'wordBoundary' | 'substring' | 'none';
const TIER_RANK: Record<MatchTier, number> = {
  exact: 0,
  prefix: 1,
  wordBoundary: 2,
  substring: 3,
  none: 4
};

function matchTier(text: string, normalizedQuery: string): MatchTier {
  const normalizedText = normalize(text);
  if (normalizedText === normalizedQuery) return 'exact';
  if (normalizedText.startsWith(normalizedQuery)) return 'prefix';
  if (matchesWordBoundary(text, normalizedQuery)) return 'wordBoundary';
  if (normalizedText.includes(normalizedQuery)) return 'substring';
  return 'none';
}

// Stage 1: ranks each option by its best tier (exact/prefix/word-boundary/substring) across
// the label AND its keywords, not the label alone - otherwise a real keyword match can get
// starved by a coincidental label substring elsewhere, and stage 2 never gets a chance to run.
// No RegExp built from the fan's input - ReDoS risk for no benefit.
function matchDeterministic(
  normalizedQuery: string,
  options: IndustryOption[]
): IndustryOption[] {
  const buckets: IndustryOption[][] = [[], [], [], []];

  for (const option of options) {
    const texts = [option.label, ...(option.keywords ?? [])];
    const bestRank = Math.min(
      ...texts.map((text) => TIER_RANK[matchTier(text, normalizedQuery)])
    );
    if (bestRank < TIER_RANK.none) buckets[bestRank].push(option);
  }

  return buckets.flat();
}

// Stage 2 (fuzzy): only reached when stage 1 finds nothing, so Fuse's relevance scoring never
// overrides stage 1's deterministic ranking - it exists purely to tolerate a typo neither an
// exact/prefix/word-boundary/substring check on the label or keywords can, e.g. "construcshun"
// for "Construction". Label is weighted above keywords.
function matchFuzzy(
  normalizedQuery: string,
  options: IndustryOption[]
): IndustryOption[] {
  const fuse = new Fuse(options, {
    ignoreLocation: true,
    threshold: 0.3,
    minMatchCharLength: 2,
    keys: [
      { name: 'label', weight: 2 },
      { name: 'keywords', weight: 1 }
    ]
  });
  return fuse.search(normalizedQuery).map((result) => result.item);
}

// Submission-time safety net, separate from the suggestion list below: an unselected query only
// counts as a pick when it's an exact match on one option's label or keyword - a match shared
// across multiple options is ambiguous, not a match. Anything else is free text for the caller.
export function findExactIndustryMatch(
  query: string,
  options: IndustryOption[] = INDUSTRY_OPTIONS
): IndustryOption | undefined {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) {
    return undefined;
  }

  const matches = options.filter(
    (option) =>
      normalize(option.label) === normalizedQuery ||
      option.keywords?.some((keyword) => normalize(keyword) === normalizedQuery)
  );

  return matches.length === 1 ? matches[0] : undefined;
}

// Single source of truth for the industry search input's suggestion list. Empty query returns
// the first MAX_RESULTS options unfiltered (the "capped open search dropdown" edge case - shown
// before the fan has typed anything). Stage 1 handles the common case, so its results aren't
// re-ranked by relevance score; stage 2 (Fuse.js) only runs when stage 1 finds nothing.
export function filterIndustries(
  query: string,
  options: IndustryOption[] = INDUSTRY_OPTIONS
): IndustryOption[] {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) {
    return options.slice(0, MAX_RESULTS);
  }

  const deterministicMatches = matchDeterministic(normalizedQuery, options);
  if (deterministicMatches.length > 0) {
    return deterministicMatches.slice(0, MAX_RESULTS);
  }

  return matchFuzzy(normalizedQuery, options).slice(0, MAX_RESULTS);
}
